import external from '@core/assets/v2/icons/arrows/regular/Icon-External-Regular.svg'

import {
    BalloonToolbar,
    BalloonPanelView,
    Bold,
    ButtonView,
    DecoupledEditor,
    Essentials,
    Italic,
    Link,
    List,
    Paragraph,
    Plugin,
    RemoveFormat,
    Strikethrough,
    Underline,
    PasteFromOffice,
    GeneralHtmlSupport,
    type Editor,
    type EditorConfig,
} from 'ckeditor5'

const EXTERNAL_ICON_SVG =
    '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.0918 6.10449C10.5457 6.15047 10.9004 6.53393 10.9004 7C10.9004 7.46607 10.5457 7.84953 10.0918 7.89551L10 7.90039H5C4.39249 7.90039 3.90039 8.39249 3.90039 9V19C3.90039 19.6075 4.39249 20.0996 5 20.0996H15C15.6075 20.0996 16.0996 19.6075 16.0996 19V14C16.0996 13.5029 16.5029 13.0996 17 13.0996C17.4971 13.0996 17.9004 13.5029 17.9004 14V19C17.9004 20.6016 16.6016 21.9004 15 21.9004H5C3.39837 21.9004 2.09961 20.6016 2.09961 19V9C2.09961 7.39837 3.39837 6.09961 5 6.09961H10L10.0918 6.10449ZM21.0918 2.10449C21.5457 2.15047 21.9004 2.53393 21.9004 3V10.2725C21.9004 10.7695 21.4971 11.1729 21 11.1729C20.5029 11.1729 20.0996 10.7695 20.0996 10.2725V5.17383L10.6367 14.6367C10.2852 14.9882 9.71475 14.9882 9.36328 14.6367C9.01181 14.2852 9.01181 13.7148 9.36328 13.3633L18.8262 3.90039H13.7275C13.2305 3.90039 12.8271 3.49706 12.8271 3C12.8271 2.50294 13.2305 2.09961 13.7275 2.09961H21L21.0918 2.10449Z" fill="currentColor"/></svg>'

function toCkeditorIcon(src: string): string {
    return src.includes('<svg') ? src : EXTERNAL_ICON_SVG
}

class OpenLinkInNewTab extends Plugin {
    public static get pluginName() {
        return 'OpenLinkInNewTab' as const
    }

    public static get requires() {
        return [Link] as const
    }

    public init() {
        const editor = this.editor
        const linkCommand = editor.commands.get('link')
        if (!linkCommand) return

        editor.ui.componentFactory.add('openLink', (locale) => {
            const button = new ButtonView(locale)
            const t = locale.t

            button.set({
                label: t('Open link in new tab'),
                icon: toCkeditorIcon(String(external)),
                tooltip: true,
            })

            button.bind('isEnabled').to(linkCommand, 'value', (href) => Boolean(href))

            this.listenTo(button, 'execute', () => {
                const href = linkCommand.value
                if (typeof href === 'string' && href) {
                    window.open(href, '_blank', 'noopener,noreferrer')
                }
            })

            return button
        })
    }
}

export const RICH_TEXT_EDITOR_PLUGINS = [
    Essentials,
    Paragraph,
    Bold,
    Italic,
    Underline,
    Strikethrough,
    List,
    Link,
    RemoveFormat,
] as const

export type FormatCommand = 'bold' | 'italic' | 'underline' | 'strikethrough' | 'bulletedList' | 'numberedList' | 'link'

export type FormatToolbarItem = {
    command: FormatCommand
    label: string
    shortcutMac?: string
    shortcutWin?: string
}

export const FORMAT_TOOLBAR_GROUPS: ReadonlyArray<ReadonlyArray<FormatToolbarItem>> = [
    [
        { command: 'bold', label: 'Bold', shortcutMac: '⌘B', shortcutWin: 'Ctrl+B' },
        { command: 'italic', label: 'Italic', shortcutMac: '⌘I', shortcutWin: 'Ctrl+I' },
        { command: 'underline', label: 'Underline', shortcutMac: '⌘U', shortcutWin: 'Ctrl+U' },
        { command: 'strikethrough', label: 'Strikethrough' },
    ],
    [
        { command: 'bulletedList', label: 'Bulleted list' },
        { command: 'numberedList', label: 'Numbered list' }
    ],
    [
        { command: 'link', label: 'Link', shortcutMac: '⌘K', shortcutWin: 'Ctrl+K' },
    ],
]

export function isMacPlatform(): boolean {
    if (typeof navigator === 'undefined') return false
    return /Mac|iPhone|iPad/.test(navigator.platform) || /Mac/.test(navigator.userAgent)
}

export function getFormatTooltip(item: FormatToolbarItem): string {
    const shortcut = isMacPlatform() ? item.shortcutMac : item.shortcutWin
    return shortcut ? `${item.label} (${shortcut})` : item.label
}

/** @deprecated Prefer FORMAT_TOOLBAR_GROUPS */
export const FORMAT_COMMANDS = FORMAT_TOOLBAR_GROUPS.flat().map(({ command, label }) => ({
    command,
    label,
    shortLabel: command.slice(0, 1).toUpperCase(),
}))

export function createEditorConfig(options: { licenseKey: string; placeholder: string }): EditorConfig {
    return {
        licenseKey: options.licenseKey,
        plugins: [Essentials, Paragraph, Bold, Italic, Underline, Strikethrough, Link, List, BalloonToolbar, PasteFromOffice, GeneralHtmlSupport, OpenLinkInNewTab],
        generalHtmlSupport: {
            allow: [
                'div',
                'span',
                'p',
                'h1',
                'h2',
                'h3',
                'h4',
                'h5',
                'h6',
                'strong',
                'em',
                'u',
                's',
                'del',
                'ul',
                'ol',
                'li',
                'blockquote',
                'code',
                'pre',
                'hr',
                'br',
                'img',
                'a',
                'table',
                'tr',
            ],
        },
        htmlSupport: {
            allow: [
                {
                    name: /.*/,
                    styles: true,
                    classes: true,
                    attributes: true
                }
            ]
        }
        ,
        balloonToolbar: {
            items: ['bold', 'italic', 'underline', 'strikethrough', '|', 'bulletedList', 'numberedList', '|', 'link'],
            shouldNotGroupWhenFull: true,
        },
        toolbar: {
            items: ['bold', 'italic', 'underline', 'strikethrough', '|', 'bulletedList', 'numberedList', '|', 'link'],
            shouldNotGroupWhenFull: true,
        },
        placeholder: options.placeholder,
        link: {
            defaultProtocol: 'https://',
            addTargetToExternalLinks: true,
            toolbar: ['openLink', '|', 'editLink', 'unlink'],
        },
    }
}

export function pinBalloonToolbarAboveSelection(editor: Editor) {
    const balloonToolbar = editor.plugins.get('BalloonToolbar') as unknown as {
        _getBalloonPositions?: (isBackward?: boolean) => unknown[]
    }

    if (!balloonToolbar?._getBalloonPositions) return

    const northPositions = Object.entries(BalloonPanelView.defaultPositions)
        .filter(([name]) => name.toLowerCase().startsWith('north'))
        .map(([, position]) => position)

    if (northPositions.length === 0) return

    balloonToolbar._getBalloonPositions = () => northPositions
}

export { DecoupledEditor }
